import { Fragment, type ReactNode } from "react";

/**
 * PHP's empty(): ACF hands back "", "0", false, null, 0 or [] for anything
 * left empty, and the theme's guards treat all of them alike.
 */
export function filled(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return !(
    value === undefined ||
    value === null ||
    value === false ||
    value === "" ||
    value === "0" ||
    value === 0
  );
}

/** A repeater field as a list; ACF stores an empty repeater as false. */
export function list<Row>(value: Row[] | false | null | undefined): Row[] {
  return Array.isArray(value) ? value : [];
}

/**
 * The sections' $render_text: *stars* highlight a phrase, new lines become
 * <br />. The pattern does not cross a new line, as PHP's did not.
 */
export function richText(value: string, highlightClass: string): ReactNode {
  return value.split("\n").map((line, index) => (
    <Fragment key={index}>
      {index > 0 && <br />}
      {line.split(/\*(.+?)\*/).map((part, partIndex) =>
        partIndex % 2 ? (
          <span key={partIndex} className={highlightClass}>
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </Fragment>
  ));
}

/**
 * A data-reveal-delay value printed the way PHP printed the float, so
 * 3 * 0.08 comes out as "0.24" rather than "0.24000000000000002".
 */
export function delay(seconds: number): string {
  return String(Math.round(seconds * 1000) / 1000);
}

type Button = { type?: string; url?: string; text?: string };

/**
 * Whether a [COMP] Button group renders: it needs a text and somewhere to go,
 * and a modal button always has somewhere to go (the theme pointed it at "#").
 */
export function hasButton<B extends Button>(
  btn: B | false | null | undefined,
): btn is B {
  return !!btn && !!btn.text && (btn.type === "trigger_modal" || !!btn.url);
}
